import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  LightModeOutlined,
  DarkModeOutlined,
  Menu as MenuIcon,
  Close,
  ExpandMore,
  ExpandLess,
  NoteAlt,
  VideoCall,
} from "@mui/icons-material";
import FlexBetween from "components/FlexBetween";
import { useDispatch } from "react-redux";
import { setMode } from "state";
import {
  AppBar,
  Button,
  Box,
  Typography,
  IconButton,
  Toolbar,
  Menu,
  MenuItem,
  Divider,
  useTheme,
  useMediaQuery,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  Snackbar,
  Slide,
} from "@mui/material";

import NotesManager from "components/NotesManager";

const BASE_URL = process.env.REACT_APP_BASE_URL || "http://localhost:9000";

// ─── Slide transition for snackbar ───────────────────────────────────────────
const SlideDown = (props) => <Slide {...props} direction="down" />;

const Navbar = ({ user, isSidebarOpen, setIsSidebarOpen }) => {
  const theme    = useTheme();
  const dispatch = useDispatch();
  const isDark   = theme.palette.mode === "dark";
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width:600px)");

  const [anchorEl, setAnchorEl]           = useState(null);
  const [username, setUsername]           = useState(localStorage.getItem("username") || "user");
  const [profileImage, setProfileImage]   = useState(localStorage.getItem("profileImage") || "");
  const [iconIsOpen, setIconIsOpen]       = useState(false);
  const [notesAnchor, setNotesAnchor]     = useState(null);
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);

  const [currentDb, setCurrentDb]                   = useState(localStorage.getItem("activeDb") || "prod");
  const [dbSwitching, setDbSwitching]               = useState(false);
  const [dbSwitchDialogOpen, setDbSwitchDialogOpen] = useState(false);
  const [pendingDb, setPendingDb]                   = useState(null);

  // ─── Live meeting alert state ─────────────────────────────────────────────
  const [liveAlerts, setLiveAlerts]     = useState([]);   // array of live meetings to show
  const [currentAlert, setCurrentAlert] = useState(null); // currently shown alert
  const seenLiveIds                     = useRef(new Set()); // track which meetings we already alerted
  const alertQueue                      = useRef([]);        // queue of pending alerts

  const isOpen      = Boolean(anchorEl);
  const accentColor = "#1565C0";
  const isSuperAdmin = String(user?.role || "").toLowerCase() === "superadmin";

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return;
    axios.get(`${BASE_URL.replace(/\/$/, "")}/api/auth/active-db`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then(({ data }) => {
      if (data.active === "dev" || data.active === "prod") {
        setCurrentDb(data.active);
        localStorage.setItem("activeDb", data.active);
      }
    }).catch(() => {});
  }, []);

  const closeDropdown = () => { setAnchorEl(null); setIconIsOpen(false); };

  const handleLogoutClick   = () => { closeDropdown(); setLogoutDialogOpen(true); };
  const handleLogoutCancel  = () => setLogoutDialogOpen(false);
  const handleLogoutConfirm = () => {
    localStorage.clear();
    setUsername("user");
    setProfileImage("");
    setLogoutDialogOpen(false);
    navigate("/user/login");
  };

  const handleProfileUpdateClick = () => { navigate("/Updateprofile"); closeDropdown(); };
  const handleMeetingClick = () => navigate("/meetings");

  const handleDbSwitchClick = () => {
    const target = currentDb === "dev" ? "prod" : "dev";
    setPendingDb(target);
    closeDropdown();
    setTimeout(() => setDbSwitchDialogOpen(true), 0);
  };

  const handleDbSwitchConfirm = async () => {
    if (!pendingDb) return;
    setDbSwitching(true);
    setDbSwitchDialogOpen(false);
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${BASE_URL}/api/auth/switch-db`,
        { db: pendingDb },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      localStorage.setItem("activeDb", pendingDb);
      window.location.reload();
    } catch (err) {
      const msg = err?.response?.data?.error_msg || err?.message || "Unknown error";
      alert(`Database switch failed:\n${msg}`);
      setDbSwitching(false);
      setPendingDb(null);
    }
  };

  const handleDbSwitchCancel = () => { setDbSwitchDialogOpen(false); setPendingDb(null); };

  useEffect(() => {
    const sync = () => {
      setUsername(localStorage.getItem("username") || "user");
      setProfileImage(localStorage.getItem("profileImage") || "");
      closeDropdown();
    };
    window.addEventListener("userUpdated", sync);
    return () => window.removeEventListener("userUpdated", sync);
  }, []);

  // ─── Decode JWT to get current user ID ───────────────────────────────────
  const getCurrentUserId = () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return null;
      const payload = token.split(".")[1];
      const decoded = JSON.parse(atob(payload));
      return decoded._id || decoded.id || null;
    } catch {
      return null;
    }
  };

  // ─── Live meeting polling ─────────────────────────────────────────────────
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return;

    const poll = async () => {
      try {
        const { data } = await axios.get(`${BASE_URL}/api/meetings`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const currentUserId = getCurrentUserId();
        const liveMeetings  = data.filter((m) => m.status === "live");

        liveMeetings.forEach((m) => {
          // Skip if already alerted
          if (seenLiveIds.current.has(m._id)) return;

          // Skip alert for the person who created/started this meeting
          const createdById = m.createdBy?._id || m.createdBy;
          if (currentUserId && String(createdById) === String(currentUserId)) {
            seenLiveIds.current.add(m._id); // mark as seen so it never alerts later
            return;
          }

          seenLiveIds.current.add(m._id);
          alertQueue.current.push(m);
        });

        // Show next alert from queue if nothing is showing
        if (alertQueue.current.length > 0 && !currentAlert) {
          setCurrentAlert(alertQueue.current.shift());
        }
      } catch {
        // silently ignore polling errors
      }
    };

    poll(); // run immediately on mount
    const interval = setInterval(poll, 10000); // then every 10 seconds
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Show next queued alert after current one closes
  useEffect(() => {
    if (!currentAlert && alertQueue.current.length > 0) {
      setTimeout(() => {
        setCurrentAlert(alertQueue.current.shift());
      }, 400);
    }
  }, [currentAlert]);

  const handleAlertClose = () => setCurrentAlert(null);

  const handleJoinNow = () => {
    if (currentAlert?.zoomJoinUrl) {
      window.open(currentAlert.zoomJoinUrl, "_blank");
    }
    setCurrentAlert(null);
  };

  const currentDbBadgeColor = currentDb === "prod" ? "#d32f2f" : "#1565C0";
  const pendingDbBadgeColor  = pendingDb  === "prod" ? "#d32f2f" : "#1565C0";

  return (
    <>
      <AppBar
        sx={{
          position: location.pathname === "/createuser" ? "static" : "sticky",
          background: isDark ? theme.palette.background.default : "#ffffff",
          boxShadow: "none",
          borderBottom: isDark
            ? "1px solid rgba(255,255,255,0.08)"
            : "1px solid rgba(0,0,0,0.08)",
          zIndex: (t) => t.zIndex.drawer + 1,
        }}
      >
        <Toolbar
          sx={{
            justifyContent: "space-between",
            minHeight: { xs: "56px", sm: "64px" },
            px: { xs: "8px", sm: "16px", md: "24px" },
          }}
        >
          {/* LEFT */}
          <FlexBetween>
            <IconButton
              onClick={() => setIsSidebarOpen(!isSidebarOpen)}
              sx={{
                color: isDark ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.6)",
                "&:hover": {
                  backgroundColor: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)",
                },
              }}
            >
              {isSidebarOpen && !isMobile ? <Close /> : <MenuIcon />}
            </IconButton>
          </FlexBetween>

          {/* RIGHT */}
          <FlexBetween gap={isMobile ? "0.25rem" : "1rem"}>

            {/* Notes icon */}
            <Tooltip title="Notes">
              <IconButton
                onClick={(e) => setNotesAnchor(notesAnchor ? null : e.currentTarget)}
                sx={{
                  color: notesAnchor ? accentColor : isDark ? "rgba(255,255,255,0.45)" : "rgba(0,0,0,0.45)",
                  "&:hover": { color: accentColor },
                  transition: "color 0.2s",
                  p: isMobile ? "6px" : "8px",
                }}
              >
                <NoteAlt sx={{ fontSize: isMobile ? "20px" : "25px" }} />
              </IconButton>
            </Tooltip>

            <NotesManager
              anchorEl={notesAnchor}
              onClose={() => setNotesAnchor(null)}
              isDark={isDark}
            />

            {/* Meeting button */}
            {!isMobile && (
              <Tooltip title="Meetings">
                <IconButton
                  onClick={handleMeetingClick}
                  sx={{
                    color: isDark ? "rgba(255,255,255,0.45)" : "rgba(0,0,0,0.45)",
                    "&:hover": { color: accentColor },
                    transition: "color 0.2s",
                  }}
                >
                  <VideoCall sx={{ fontSize: "25px" }} />
                </IconButton>
              </Tooltip>
            )}

            {/* Theme toggle */}
            <IconButton
              onClick={() => dispatch(setMode())}
              sx={{
                color: isDark ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.6)",
                "&:hover": {
                  backgroundColor: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)",
                },
                p: isMobile ? "6px" : "8px",
              }}
            >
              {isDark
                ? <LightModeOutlined sx={{ fontSize: isMobile ? "20px" : "25px" }} />
                : <DarkModeOutlined  sx={{ fontSize: isMobile ? "20px" : "25px" }} />}
            </IconButton>

            {/* Profile dropdown */}
            <FlexBetween gap="0">
              <Button
                onClick={(e) => { setIconIsOpen(!iconIsOpen); setAnchorEl(e.currentTarget); }}
                sx={{
                  textTransform: "none", color: "inherit", minWidth: 0,
                  p: isMobile ? "4px 6px" : "6px 8px",
                  borderRadius: "10px",
                  "&:hover": {
                    backgroundColor: isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)",
                  },
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: isMobile ? "0" : "10px" }}>
                  {profileImage ? (
                    <img
                      src={profileImage} alt="Profile"
                      style={{
                        height: isMobile ? "28px" : "32px",
                        width:  isMobile ? "28px" : "32px",
                        borderRadius: "50%", objectFit: "cover",
                      }}
                    />
                  ) : (
                    <Box sx={{
                      height: isMobile ? 28 : 32, width: isMobile ? 28 : 32,
                      borderRadius: "50%", backgroundColor: accentColor,
                      color: "#fff", display: "flex",
                      justifyContent: "center", alignItems: "center",
                      fontSize: isMobile ? "18px" : "22px",
                      fontWeight: "600", flexShrink: 0,
                    }}>
                      {username.charAt(0).toUpperCase()}
                    </Box>
                  )}
                  {!isMobile && (
                    <Box textAlign="left" display="flex" flexDirection="column" ml={1}>
                      <Typography variant="caption" sx={{ color: accentColor, fontWeight: 500, lineHeight: 1 }}>
                        Welcome
                      </Typography>
                      <Typography fontSize="0.85rem" fontWeight="400" sx={{ color: accentColor, lineHeight: 1.3 }}>
                        {username}
                      </Typography>
                    </Box>
                  )}
                  <Box sx={{ ml: isMobile ? 0 : "4px", color: isDark ? "rgba(255,255,255,0.6)" : "rgba(0,0,0,0.5)" }}>
                    {iconIsOpen
                      ? <ExpandLess sx={{ fontSize: isMobile ? "18px" : "20px" }} />
                      : <ExpandMore sx={{ fontSize: isMobile ? "18px" : "20px" }} />}
                  </Box>
                </Box>
              </Button>

              <Menu
                anchorEl={anchorEl} open={isOpen} onClose={closeDropdown}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ horizontal: "right", vertical: "top" }}
                PaperProps={{
                  elevation: 0,
                  sx: {
                    overflow: "visible", mt: 1.5,
                    backgroundColor: theme.palette.background.paper,
                    color: theme.palette.text.primary,
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: "10px",
                    boxShadow: theme.shadows[8],
                    minWidth: 220,
                    "&:before": {
                      content: '""', display: "block", position: "absolute",
                      top: 0, right: 14, width: 10, height: 10,
                      bgcolor: "background.paper",
                      transform: "translateY(-50%) rotate(45deg)", zIndex: 0,
                      borderLeft: `1px solid ${theme.palette.divider}`,
                      borderTop: `1px solid ${theme.palette.divider}`,
                    },
                  },
                }}
              >
                {isMobile && (
                  <MenuItem disabled sx={{ fontSize: "0.85rem", fontWeight: 400, color: accentColor, opacity: "1 !important" }}>
                    {username}
                  </MenuItem>
                )}
                <MenuItem onClick={handleProfileUpdateClick}
                  sx={{ fontSize: "0.875rem", fontWeight: 400,
                    color: isDark ? "rgba(255,255,255,0.85)" : "rgba(0,0,0,0.78)",
                    "&:hover": { backgroundColor: theme.palette.action.hover } }}>
                  My Profile
                </MenuItem>
                {isMobile && (
                  <MenuItem onClick={() => { handleMeetingClick(); closeDropdown(); }}
                    sx={{ fontSize: "0.875rem", fontWeight: 400,
                      color: isDark ? "rgba(255,255,255,0.85)" : "rgba(0,0,0,0.78)",
                      "&:hover": { backgroundColor: theme.palette.action.hover } }}>
                    Meetings
                  </MenuItem>
                )}
                <Divider sx={{ my: 0.5 }} />
                {isSuperAdmin && <MenuItem onClick={handleDbSwitchClick} disabled={dbSwitching}
                  sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2,
                    "&:hover": { backgroundColor: theme.palette.action.hover } }}>
                  <Typography variant="body2"
                    sx={{ fontSize: "0.875rem", fontWeight: 400,
                      color: isDark ? "rgba(255,255,255,0.85)" : "rgba(0,0,0,0.78)" }}>
                    {dbSwitching ? "Switching…" : `Switch to ${currentDb === "dev" ? "PROD" : "DEV"}`}
                  </Typography>
                  <Box component="span"
                    sx={{ fontSize: "0.65rem", fontWeight: 700, px: "8px", py: "3px",
                      borderRadius: "5px", letterSpacing: "0.6px",
                      backgroundColor: currentDbBadgeColor, color: "#fff", flexShrink: 0,
                      boxShadow: `0 0 0 2px ${currentDbBadgeColor}33` }}>
                    {currentDb.toUpperCase()}
                  </Box>
                </MenuItem>}
                <Divider sx={{ my: 0.5 }} />
                <MenuItem onClick={handleLogoutClick}
                  sx={{ fontSize: "0.875rem", fontWeight: 400, color: "#e53935",
                    "&:hover": { backgroundColor: theme.palette.action.hover } }}>
                  Logout
                </MenuItem>
              </Menu>
            </FlexBetween>
          </FlexBetween>
        </Toolbar>
      </AppBar>

      {/* ─── Live Meeting Alert Snackbar ──────────────────────────────────────── */}
      <Snackbar
        open={Boolean(currentAlert)}
        onClose={handleAlertClose}
        TransitionComponent={SlideDown}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        autoHideDuration={15000}
        sx={{ top: { xs: "70px", sm: "76px" }, zIndex: 9999 }}
      >
        <Box sx={{
          display: "flex", alignItems: "center",
          gap: { xs: "10px", sm: "16px" },
          px: { xs: "14px", sm: "20px" },
          py: "12px",
          borderRadius: "14px",
          backgroundColor: isDark ? "#1a2744" : "#ffffff",
          border: "1.5px solid #4caf50",
          boxShadow: "0 8px 32px rgba(76,175,80,0.25)",
          minWidth: { xs: "300px", sm: "420px" },
          maxWidth: { xs: "92vw", sm: "520px" },
          flexWrap: { xs: "wrap", sm: "nowrap" },
        }}>
          {/* Pulsing green dot */}
          <Box sx={{
            width: 10, height: 10, borderRadius: "50%",
            backgroundColor: "#4caf50", flexShrink: 0,
            animation: "navPulse 1.5s ease-in-out infinite",
            "@keyframes navPulse": {
              "0%, 100%": { opacity: 1, transform: "scale(1)" },
              "50%":       { opacity: 0.5, transform: "scale(1.4)" },
            },
          }} />

          {/* Text */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{
              fontSize: "0.8rem", fontWeight: 700,
              color: "#4caf50", letterSpacing: "0.3px", lineHeight: 1.2,
            }}>
              🔴 Meeting is Live Now!
            </Typography>
            <Typography sx={{
              fontSize: "0.78rem", fontWeight: 500,
              color: isDark ? "#e2e8f0" : "#0d1b2a",
              mt: "2px", lineHeight: 1.3,
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            }}>
              {currentAlert?.title}
            </Typography>
            {currentAlert?.topic && (
              <Typography sx={{
                fontSize: "0.68rem", color: isDark ? "#94a3b8" : "#546e7a", mt: "1px",
              }}>
                {currentAlert.topic}
              </Typography>
            )}
          </Box>

          {/* Join Now button */}
          <Button
            size="small"
            onClick={handleJoinNow}
            startIcon={<VideoCall sx={{ fontSize: 15 }} />}
            sx={{
              borderRadius: "8px", textTransform: "none",
              fontWeight: 700, fontSize: "0.75rem",
              px: "14px", py: "6px", flexShrink: 0,
              backgroundColor: "#4caf50", color: "#fff",
              boxShadow: "0 2px 8px rgba(76,175,80,0.35)",
              "&:hover": { backgroundColor: "#388e3c" },
            }}
          >
            Join Now
          </Button>

          {/* Dismiss X */}
          <IconButton
            size="small"
            onClick={handleAlertClose}
            sx={{
              width: 24, height: 24, flexShrink: 0,
              color: isDark ? "#94a3b8" : "#546e7a",
              "&:hover": { color: isDark ? "#e2e8f0" : "#0d1b2a" },
            }}
          >
            <Close sx={{ fontSize: 14 }} />
          </IconButton>
        </Box>
      </Snackbar>

      {/* ─── Logout Dialog ────────────────────────────────────────────────────── */}
      <Dialog open={logoutDialogOpen} onClose={handleLogoutCancel} disableRestoreFocus
        PaperProps={{ sx: { borderRadius: "12px", padding: "8px",
          backgroundColor: theme.palette.background.paper,
          minWidth: isMobile ? "90vw" : "320px", maxWidth: "400px" } }}>
        <DialogTitle sx={{ fontWeight: 600, fontSize: "1.05rem",
          color: isDark ? "rgba(255,255,255,0.92)" : "rgba(0,0,0,0.82)" }}>
          Confirm Logout
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: "0.875rem",
            color: isDark ? "rgba(255,255,255,0.6)" : "rgba(0,0,0,0.55)", lineHeight: 1.6 }}>
            Are you sure you want to logout? You will be redirected to the login page.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={handleLogoutCancel} variant="outlined"
            sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 500,
              color: isDark ? "rgba(255,255,255,0.75)" : "rgba(0,0,0,0.6)",
              borderColor: isDark ? "rgba(255,255,255,0.25)" : "rgba(0,0,0,0.22)" }}>
            Cancel
          </Button>
          <Button onClick={handleLogoutConfirm} variant="contained" color="error"
            sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 600 }}>
            Yes, Logout
          </Button>
        </DialogActions>
      </Dialog>

      {/* ─── DB Switch Dialog ─────────────────────────────────────────────────── */}
      <Dialog open={dbSwitchDialogOpen} onClose={handleDbSwitchCancel} disableRestoreFocus
        PaperProps={{ sx: { borderRadius: "12px", padding: "8px",
          backgroundColor: theme.palette.background.paper,
          minWidth: isMobile ? "90vw" : "340px", maxWidth: "420px" } }}>
        <DialogTitle sx={{ fontWeight: 600, fontSize: "1.05rem",
          color: isDark ? "rgba(255,255,255,0.92)" : "rgba(0,0,0,0.82)" }}>
          Switch Database
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: "0.875rem",
            color: isDark ? "rgba(255,255,255,0.6)" : "rgba(0,0,0,0.55)", lineHeight: 1.7 }}>
            You are switching from{" "}
            <Box component="span" sx={{ fontWeight: 700, color: "#fff",
              backgroundColor: currentDbBadgeColor, px: "6px", py: "1px",
              borderRadius: "4px", fontSize: "0.8rem" }}>
              {currentDb.toUpperCase()}
            </Box>{" "}to{" "}
            <Box component="span" sx={{ fontWeight: 700, color: "#fff",
              backgroundColor: pendingDbBadgeColor, px: "6px", py: "1px",
              borderRadius: "4px", fontSize: "0.8rem" }}>
              {pendingDb?.toUpperCase()}
            </Box>
            . All subsequent data operations will use the new database.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={handleDbSwitchCancel} variant="outlined"
            sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 500,
              color: isDark ? "rgba(255,255,255,0.75)" : "rgba(0,0,0,0.6)",
              borderColor: isDark ? "rgba(255,255,255,0.25)" : "rgba(0,0,0,0.22)" }}>
            Cancel
          </Button>
          <Button onClick={handleDbSwitchConfirm} variant="contained"
            sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 600,
              backgroundColor: pendingDbBadgeColor,
              "&:hover": { backgroundColor: pendingDb === "prod" ? "#b71c1c" : "#0d47a1" } }}>
            Yes, Switch to {pendingDb?.toUpperCase()}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Navbar;
